import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { KeywordPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/keyword-monitoring')({
  component: KeywordMonitoringPage,
  head: () => ({
    meta: [
      { title: 'Keyword Monitoring — Content Change Detection | Uptrack' },
      { name: 'description', content: 'Monitor websites for keyword presence or absence. Detect defacement, verify content delivery, and get alerted when page content changes unexpectedly.' },
      { property: 'og:title', content: 'Keyword Monitoring — Content Change Detection | Uptrack' },
      { property: 'og:description', content: 'Monitor for keyword presence or absence. Detect defacement and content changes. Included on every plan, free plan too.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-keyword-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/keyword-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Keyword Monitoring', url: 'https://uptrack.app/features/keyword-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Keyword Monitoring',
        description: 'Website keyword and content monitoring with presence/absence detection and defacement alerts.',
        url: 'https://uptrack.app/features/keyword-monitoring',
      })),
    ],
  }),
})

function KeywordMonitoringPage() {
  return (
    <FeaturePage
      section='Keyword and content monitoring'
      title='Keyword monitoring for content you care about.'
      intro='Check for the presence or absence of specific words on any page. Detect defacement, verify content delivery, and catch unexpected changes.'
      featuresHeading='Beyond simple uptime checks'
      features={[
    { title: 'Presence check', desc: 'Alert when an expected keyword disappears from a page. Verify your app still renders the right content.' },
    { title: 'Absence check', desc: 'Alert when an unwanted keyword appears. Detect defacement, error messages, or injected content.' },
    { title: 'Content delivery verification', desc: 'Confirm your CDN serves the right content. Catch stale caches or misrouted deployments.' },
    { title: 'Works with HTTP checks', desc: 'Add keyword checks to any HTTP monitor. Get uptime + content verification in a single check.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Keyword monitoring catches problems that HTTP status codes miss entirely.',
        columns: ['Provider', 'Keyword Check', 'Absence Check', 'Included'],
        rows: [
      { cells: ['UptimeRobot', 'Yes', 'Yes', 'Yes'] },
      { cells: ['Better Stack', 'Yes', 'No', 'Limited'] },
      { cells: ['Pingdom', 'Paid', 'No', 'No free plan'] },
      { cells: ['Uptrack', 'Yes', 'Yes', 'Every plan, free included'], highlight: true },
        ],
      }}
      why={{ heading: 'Why keyword monitoring matters', body: 'A server can return HTTP 200 while serving a blank page, an error message, or defaced content. Status codes only tell you the server responded — keyword checks tell you the response is correct. It is the difference between "the server is up" and "the app actually works."' }}
      cta={{ heading: 'Start monitoring your content now', body: 'Every plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <KeywordPiece />
        </ProductFrame>
      }
    />
  )
}
